import React, { FormEvent, useEffect, useState } from "react";
import styled from "styled-components";
import * as Sentry from "@sentry/react";
import Select from "../../components/parts/Select";
import Pagination from "../../components/parts/Pagination";
import AdministratorShopList from "../../components/combinations/Administrator/AdministratorShopList";
import { GetAdministratorShops200ResponseInner } from "../../../api/v1";
import { api } from "../../../api";
import TextField from "../../components/parts/TextField";
import Button from "../../components/parts/Button";
import MultipleSelect from "../../components/parts/MultipleSelect";
import { useLocation, useNavigate } from "react-router";
import { BiSmile, BiTime, BiYen } from "react-icons/bi";
import AdministratorLayout from "../../components/layouts/AdministratorLayout";

const ShopsStyle = styled.div`
  background: white;
  border-radius: 25px;
  padding: 20px 0;
  margin: 30px;

  @media (max-width: 767px) {
    margin: 30px 10px;
  }
`;

const OverviewStyle = styled.div`
  margin: 0 30px 10px 30px;

  @media (max-width: 767px) {
    margin: 0 12px 10px 12px;
  }

  h2 {
    color: #383432;
    display: inline-block;
    font-size: 18px;
    font-weight: bold;
    margin: 0 10px 0 0;
  }

  p {
    display: inline-block;
    font-size: 10px;
    margin: 0;
  }

  span {
    font-size: 34px;
  }
`;

const SearchFormStyle = styled.form`
  margin: 0 30px 30px 30px;

  @media (max-width: 767px) {
    margin: 0 12px 10px 12px;
  }
`;

const SearchFormRowStyle = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`;

const SearchButtonStyle = styled(Button)`
  flex-shrink: 0;
`;

const Dashboard: React.FC = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const page = Number(searchParams.get("page")) || 1;
  const timeframes = [
    { id: "morning", value: "モーニング" },
    { id: "lunch", value: "ランチ" },
    { id: "dinner", value: "ディナー" },
  ];
  const priceRanges = [
    { id: "1000", value: "1000" },
    { id: "2000", value: "2000" },
    { id: "3000", value: "3000" },
    { id: "4000", value: "4000" },
    { id: "5000", value: "5000" },
  ];
  const [shops, setShops] = useState<
    Array<GetAdministratorShops200ResponseInner>
  >([]);
  const [area, setArea] = useState<string>(searchParams.get("area") || "");
  const [shopName, setShopName] = useState<string>(
    searchParams.get("shopName") || ""
  );
  const [timeframe, setTimeframe] = useState<string>(
    searchParams.get("timeframe") || ""
  );
  const [upperBudget, setUpperBudget] = useState<string>(
    searchParams.get("upperBudget") || ""
  );
  const [selectFeatures, setSelectFeatures] = useState<Array<string>>(
    searchParams.get("featureIds")?.split(",") || []
  );
  const [features, setFeatures] = useState([]);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>();
  const [pageNumbers, setPageNumbers] = useState<Array<number>>([]);
  const [totalCount, setTotalCount] = useState<string>();
  const [params, setParams] = useState<string>();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let timeframeParam;
    switch (timeframe) {
      case "morning":
        timeframeParam = "morning";
        break;
      case "lunch":
        timeframeParam = "lunch";
        break;
      case "dinner":
        timeframeParam = "dinner";
        break;
      default:
        timeframeParam = null;
    }

    api
      .getAdministratorShops(
        page,
        area,
        shopName,
        timeframeParam,
        upperBudget,
        selectFeatures
      )
      .then((response) => {
        setShops(response.data);
        setCurrentPageNumber(Number(response.headers["x-current-page"]));
        setTotalCount(response.headers["x-total-count"] || 0);
        const totalPages = Number(response.headers["x-total-pages"]) || 0;
        const pageList = [...Array(totalPages)].map((_, i) => i + 1);
        setPageNumbers(pageList);
      })
      .catch((error) => {
        if (error.response?.status === 401) navigate("/administrator/login");
        Sentry.captureException(error);
      });
    api.getFeatures().then((response) => {
      setFeatures(response.data);
    });
  }, [location.search]);

  // NOTE: ページネーションリンクにクエリパラメーターを付与するために必要
  useEffect(() => {
    const urlSearchParam = new URLSearchParams({
      area,
      shopName,
      timeframe,
      upperBudget,
    }).toString();
    setParams(urlSearchParam);
  }, [area, shopName, timeframe, upperBudget]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const params = {};
    if (area) params["area"] = area;
    if (shopName) params["shopName"] = shopName;
    if (timeframe) params["timeframe"] = timeframe;
    if (selectFeatures.length) params["featureIds"] = selectFeatures;
    if (upperBudget) params["upperBudget"] = upperBudget;
    const urlSearchParam = new URLSearchParams(params).toString();
    navigate("/administrator/dashboard?" + urlSearchParam);
  };

  return (
    <AdministratorLayout>
      <ShopsStyle>
        <OverviewStyle>
          <h2>検索結果</h2>
          <p>
            <span>{totalCount}</span>件
          </p>
        </OverviewStyle>
        <SearchFormStyle onSubmit={handleSubmit}>
          <SearchFormRowStyle>
            <TextField
              placeholder="エリア・駅"
              value={area}
              onChange={(event) => setArea(event.target.value)}
            />
            <TextField
              placeholder="店名"
              value={shopName}
              onChange={(event) => setShopName(event.target.value)}
            />
            <SearchButtonStyle>検索</SearchButtonStyle>
          </SearchFormRowStyle>
          <SearchFormRowStyle>
            <Select
              label="時間帯"
              items={timeframes}
              selectItem={timeframe}
              setSelectItemAction={setTimeframe}
              icon={<BiTime />}
              nullValue={true}
            />
            <Select
              label="価格帯"
              items={priceRanges}
              selectItem={upperBudget}
              setSelectItemAction={setUpperBudget}
              icon={<BiYen />}
              prefix="~"
              nullValue={true}
            />
            <MultipleSelect
              label="特徴"
              items={features.map((feature) => {
                return { key: feature.id.toString(), value: feature.name };
              })}
              selectItemsState={selectFeatures}
              setSelectItemsAction={setSelectFeatures}
              icon={<BiSmile />}
            />
          </SearchFormRowStyle>
        </SearchFormStyle>
        <AdministratorShopList shops={shops} />
        <Pagination
          numbers={[
            // NOTE:  pageNumbersが1始まりなため配列の真ん中がcurrentPageNumberと一致するように調整
            pageNumbers[currentPageNumber - 3],
            pageNumbers[currentPageNumber - 2],
            pageNumbers[currentPageNumber - 1],
            pageNumbers[currentPageNumber],
            pageNumbers[currentPageNumber + 1],
          ]}
          currentPageNumber={currentPageNumber}
          url={window.location.origin + window.location.pathname}
          params={params}
        />
      </ShopsStyle>
    </AdministratorLayout>
  );
};
export default Dashboard;
